<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('crontab.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('crontab.title') }}</h1>
      <p>{{ $t('crontab.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="cron-input-section">
        <label>{{ $t('crontab.expressionLabel') }}</label>
        <input
          v-model="expression"
          class="cron-input"
          placeholder="* * * * *"
          spellcheck="false"
          @input="parseCron"
        />
        <p class="cron-desc" v-if="description">{{ description }}</p>
        <p class="cron-error" v-if="cronError">{{ cronError }}</p>
      </div>

      <div class="cron-fields">
        <div class="field" v-for="f in fields" :key="f.key">
          <div class="field-label">{{ f.label }}</div>
          <div class="field-value">{{ f.value }}</div>
          <div class="field-range">{{ f.range }}</div>
        </div>
      </div>

      <div class="quick-presets">
        <label>{{ $t('crontab.presets') }}</label>
        <div class="preset-grid">
          <button v-for="p in presets" :key="p.cron" class="preset-btn" @click="applyPreset(p.cron)">
            <code>{{ p.cron }}</code>
            <span>{{ p.desc }}</span>
          </button>
        </div>
      </div>

      <div v-if="nextRuns.length > 0" class="next-runs">
        <label>{{ $t('crontab.nextRunsLabel') }}</label>
        <ol>
          <li v-for="(run, i) in nextRuns" :key="i">{{ run }}</li>
        </ol>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('crontab.whatIs') }}</h2>
      <p>{{ $t('crontab.whatIsDesc') }}</p>
      <h2>{{ $t('crontab.syntaxTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('crontab.syntaxItems')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('crontab.tipsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('crontab.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrontabParser',
  data() {
    return {
      expression: '0 9 * * 1-5',
      description: '',
      cronError: '',
      fields: [],
      nextRuns: []
    }
  },
  computed: {
    presets() {
      return [
        { cron: '* * * * *', desc: this.$t('crontab.preset.everyMinute') },
        { cron: '0 * * * *', desc: this.$t('crontab.preset.everyHour') },
        { cron: '0 0 * * *', desc: this.$t('crontab.preset.everyDay') },
        { cron: '0 9 * * 1-5', desc: this.$t('crontab.preset.weekday9am') },
        { cron: '0 0 * * 0', desc: this.$t('crontab.preset.everySunday') },
        { cron: '0 0 1 * *', desc: this.$t('crontab.preset.everyMonth') },
        { cron: '*/5 * * * *', desc: this.$t('crontab.preset.every5min') },
        { cron: '0 */2 * * *', desc: this.$t('crontab.preset.every2hours') },
        { cron: '0 0 * * 1', desc: this.$t('crontab.preset.everyMonday') },
        { cron: '0 0 1 1 *', desc: this.$t('crontab.preset.everyYear') }
      ]
    }
  },
  methods: {
    applyPreset(cron) {
      this.expression = cron
      this.parseCron()
    },
    parseCron() {
      this.cronError = ''
      this.description = ''
      this.fields = []
      this.nextRuns = []

      const parts = this.expression.trim().split(/\s+/)
      if (parts.length !== 5) {
        this.cronError = this.$t('crontab.errorFormat')
        return
      }

      const fieldDefs = [
        { key: 'minute', label: this.$t('crontab.field.minute'), range: '0-59', value: parts[0] },
        { key: 'hour', label: this.$t('crontab.field.hour'), range: '0-23', value: parts[1] },
        { key: 'day', label: this.$t('crontab.field.day'), range: '1-31', value: parts[2] },
        { key: 'month', label: this.$t('crontab.field.month'), range: '1-12', value: parts[3] },
        { key: 'weekday', label: this.$t('crontab.field.weekday'), range: '0-7 (0,7=Sun)', value: parts[4] }
      ]
      this.fields = fieldDefs

      try {
        this.description = this.describeCron(parts)
        this.nextRuns = this.calculateNextRuns(parts, 10)
      } catch (e) {
        this.cronError = e.message
      }
    },
    describeCron(parts) {
      const [min, hour, day, month, weekday] = parts
      const pieces = []

      if (min === '*' && hour === '*' && day === '*' && month === '*' && weekday === '*') {
        return this.$t('crontab.desc.everyMinute')
      }

      // 分钟
      if (min.startsWith('*/')) pieces.push(this.$t('crontab.desc.everyNMin').replace('{n}', min.slice(2)))
      else if (min !== '*') pieces.push(this.$t('crontab.desc.atMinute').replace('{n}', min))

      // 小时
      if (hour.startsWith('*/')) pieces.push(this.$t('crontab.desc.everyNHour').replace('{n}', hour.slice(2)))
      else if (hour !== '*') pieces.push(this.$t('crontab.desc.atHour').replace('{n}', hour))

      // 日
      if (day.startsWith('*/')) pieces.push(this.$t('crontab.desc.everyNDay').replace('{n}', day.slice(2)))
      else if (day !== '*') pieces.push(this.$t('crontab.desc.onDay').replace('{n}', day))

      // 月
      if (month !== '*') pieces.push(this.$t('crontab.desc.inMonth').replace('{n}', month))

      // 周
      const weekNames = { '0': this.$t('crontab.week.sun'), '1': this.$t('crontab.week.mon'), '2': this.$t('crontab.week.tue'), '3': this.$t('crontab.week.wed'), '4': this.$t('crontab.week.thu'), '5': this.$t('crontab.week.fri'), '6': this.$t('crontab.week.sat'), '7': this.$t('crontab.week.sun') }
      if (weekday !== '*') {
        if (weekday.includes('-')) {
          const [start, end] = weekday.split('-')
          pieces.push((weekNames[start] || start) + '-' + (weekNames[end] || end))
        } else if (weekday.includes(',')) {
          pieces.push(weekday.split(',').map(w => weekNames[w] || w).join(', '))
        } else {
          pieces.push(weekNames[weekday] || this.$t('crontab.desc.onWeekday').replace('{n}', weekday))
        }
      }

      return pieces.join(', ')
    },
    expandField(field, min, max) {
      if (field === '*') {
        const arr = []
        for (let i = min; i <= max; i++) arr.push(i)
        return arr
      }
      const result = new Set()
      for (const part of field.split(',')) {
        if (part.includes('/')) {
          const [range, step] = part.split('/')
          const s = parseInt(step)
          let start = min
          if (range !== '*') start = parseInt(range)
          for (let i = start; i <= max; i += s) result.add(i)
        } else if (part.includes('-')) {
          const [a, b] = part.split('-').map(Number)
          for (let i = a; i <= b; i++) result.add(i)
        } else {
          result.add(parseInt(part))
        }
      }
      return [...result].sort((a, b) => a - b)
    },
    calculateNextRuns(parts, count) {
      const [minF, hourF, dayF, monthF, weekdayF] = parts
      const minutes = this.expandField(minF, 0, 59)
      const hours = this.expandField(hourF, 0, 23)
      const days = this.expandField(dayF, 1, 31)
      const months = this.expandField(monthF, 1, 12)
      const weekdays = this.expandField(weekdayF, 0, 7).map(d => d === 7 ? 0 : d)

      const runs = []
      const now = new Date()
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes() + 1, 0, 0)

      let safety = 0
      while (runs.length < count && safety < 525960) { // max ~1 year of minutes
        safety++
        const mo = d.getMonth() + 1
        const da = d.getDate()
        const wd = d.getDay()
        const hr = d.getHours()
        const mi = d.getMinutes()

        if (months.includes(mo) && days.includes(da) && weekdays.includes(wd) && hours.includes(hr) && minutes.includes(mi)) {
          runs.push(d.toLocaleString())
        }
        d.setMinutes(d.getMinutes() + 1)
      }
      return runs
    }
  },
  mounted() {
    this.parseCron()
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.cron-input-section { margin-bottom: 20px; }
.cron-input-section label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: block; }
.cron-input { width: 100%; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 14px 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 20px; text-align: center; letter-spacing: 4px; box-sizing: border-box; outline: none; }
.cron-input:focus { border-color: var(--accent-color); }
.cron-desc { margin-top: 10px; font-size: 15px; color: var(--accent-color); text-align: center; font-weight: 500; }
.cron-error { margin-top: 10px; font-size: 14px; color: #ef4444; text-align: center; }
.cron-fields { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; justify-content: center; }
.field { text-align: center; padding: 12px 16px; background: var(--bg-tertiary); border-radius: 10px; min-width: 100px; border: 1px solid var(--border-color); }
.field-label { font-size: 12px; color: var(--text-secondary); margin-bottom: 4px; }
.field-value { font-family: monospace; font-size: 18px; color: var(--accent-color); font-weight: 700; margin-bottom: 4px; }
.field-range { font-size: 11px; color: var(--text-muted); }
.quick-presets { margin-bottom: 20px; }
.quick-presets > label { font-size: 14px; color: var(--text-secondary); margin-bottom: 10px; display: block; font-weight: 600; }
.preset-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px; }
.preset-btn { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 8px; cursor: pointer; transition: all 0.2s; text-align: left; }
.preset-btn:hover { border-color: var(--accent-color); background: var(--bg-hover); }
.preset-btn code { font-family: monospace; font-size: 12px; color: var(--accent-color); background: var(--accent-light); padding: 3px 8px; border-radius: 4px; white-space: nowrap; }
.preset-btn span { font-size: 13px; color: var(--text-secondary); }
.next-runs { margin-bottom: 10px; }
.next-runs > label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: block; font-weight: 600; }
.next-runs ol { padding-left: 24px; background: var(--bg-tertiary); border-radius: 8px; padding: 16px 16px 16px 40px; border: 1px solid var(--border-color); }
.next-runs li { font-family: monospace; font-size: 14px; color: var(--text-primary); line-height: 2; }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
@media (max-width: 768px) { .preset-grid { grid-template-columns: 1fr; } .cron-fields { flex-direction: column; align-items: center; } }
</style>
